<script setup lang="ts">
import { ref, watch } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { classesApi } from '@/api'
import type { ClassDetails, CreateClassRequest } from '@/api/api'
import { ASSIGNABLE_COLOURS, COLOUR_MAP, type PaletteColour } from '@/constants/colors'
import { AppButton } from '@/design-system/components'
import { getErrorMessage } from '@/utils/error'
import { useGating } from '@/composables/useGating'
import { useAnalytics } from '@/composables/useAnalytics'

/**
 * CreateClassModal — extracted from the old class list / home create flow so
 * the §04 overview ("New class" button + Add card) shares one implementation.
 */
const props = defineProps<{ modelValue: boolean }>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  created: [cls: ClassDetails]
}>()

const { atClassCap, showPaywall, handleQuotaError, refreshUsage } = useGating()
const { capture } = useAnalytics()

const form = ref<CreateClassRequest>({ name: '', colour: 'RED' as PaletteColour })
const saving = ref(false)
const error = ref<string | null>(null)

watch(
  () => props.modelValue,
  (open) => {
    if (open) {
      if (atClassCap.value) {
        emit('update:modelValue', false)
        showPaywall('classes')
        return
      }
      form.value = { name: '', colour: 'RED' as PaletteColour }
      error.value = null
    }
  },
)

function close() {
  emit('update:modelValue', false)
}

async function submit() {
  if (!form.value.name?.trim()) {
    error.value = 'Please enter a class name'
    return
  }
  saving.value = true
  error.value = null
  try {
    const res = await classesApi.classesPost(form.value)
    if (res.data) {
      // Web classes carry no room concept (rooms live on lessons/slots), so
      // has_room is structurally false — kept for cross-platform schema parity.
      capture('class_created', { has_room: false })
      void refreshUsage()
      emit('created', res.data)
      close()
    }
  } catch (err: unknown) {
    if (handleQuotaError('classes', err)) close()
    else error.value = getErrorMessage(err, 'Failed to create class.')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="ccm-overlay">
      <div class="ccm-backdrop" @click="close" />
      <div class="ccm-card">
        <div class="ccm-header">
          <h2>Create new class</h2>
          <button class="ccm-close" @click="close">
            <FontAwesomeIcon :icon="['fas', 'xmark']" />
          </button>
        </div>
        <form class="ccm-body" @submit.prevent="submit">
          <div>
            <label for="ccmName" class="ccm-label">Class name</label>
            <input
              id="ccmName"
              v-model="form.name"
              type="text"
              placeholder="e.g. 8 Oasis"
              class="ccm-input"
              :disabled="saving"
            />
          </div>
          <div>
            <label class="ccm-label">Accent colour</label>
            <div class="ccm-grid">
              <button
                v-for="colour in ASSIGNABLE_COLOURS"
                :key="colour"
                type="button"
                class="ccm-swatch"
                :class="{ active: form.colour === colour }"
                :style="{ backgroundColor: COLOUR_MAP[colour].hex }"
                :title="COLOUR_MAP[colour].name"
                @click="form.colour = colour"
              >
                <FontAwesomeIcon v-if="form.colour === colour" :icon="['fas', 'check']" />
              </button>
            </div>
          </div>
          <div v-if="error" class="ccm-error">{{ error }}</div>
          <div class="ccm-actions">
            <AppButton variant="secondary" size="md" class="ccm-flex" :disabled="saving" @click="close">
              Cancel
            </AppButton>
            <AppButton
              variant="primary"
              size="md"
              type="submit"
              class="ccm-flex"
              :loading="saving"
              :disabled="!form.name?.trim()"
            >
              Create class
            </AppButton>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.ccm-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-lg);
}
.ccm-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(12, 12, 14, 0.5);
  backdrop-filter: blur(4px);
}
.ccm-card {
  position: relative;
  background: var(--surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-modal);
  width: 100%;
  max-width: 28rem;
  overflow: hidden;
}
.ccm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-lg) var(--space-xl);
  border-bottom: 1px solid var(--hairline);
}
.ccm-header h2 {
  font-size: var(--text-title-md);
  font-weight: var(--weight-semibold);
  color: var(--ink-900);
  margin: 0;
}
.ccm-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);
  color: var(--ink-500);
  cursor: pointer;
}
.ccm-close:hover {
  background: var(--surface-muted);
  color: var(--ink-900);
}
.ccm-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
  padding: var(--space-xl);
}
.ccm-label {
  display: block;
  font-size: var(--text-body-sm);
  font-weight: var(--weight-medium);
  color: var(--ink-700);
  margin-bottom: var(--space-6);
}
.ccm-input {
  width: 100%;
  padding: var(--space-sm) var(--space-md);
  background: var(--surface);
  border: 1px solid var(--outline-variant);
  border-radius: var(--radius-md);
  font-size: var(--text-body);
  color: var(--ink-900);
  font-family: inherit;
}
.ccm-input:focus {
  outline: 2px solid var(--focus-ring);
  outline-offset: -1px;
  border-color: transparent;
}
.ccm-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: var(--space-sm);
}
.ccm-swatch {
  aspect-ratio: 1;
  width: 100%;
  border: 0;
  border-radius: var(--radius-md);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: transform var(--motion-fast);
}
.ccm-swatch:hover {
  transform: scale(1.05);
}
.ccm-swatch.active {
  outline: 2px solid var(--ink-black);
  outline-offset: 2px;
  transform: scale(1.05);
}
.ccm-error {
  background: var(--danger-soft);
  border-radius: var(--radius-md);
  padding: var(--space-md);
  color: var(--danger-ink);
  font-size: var(--text-body-sm);
}
.ccm-actions {
  display: flex;
  gap: var(--space-md);
}
.ccm-flex {
  flex: 1;
}
</style>
